import { ComponentType } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { IconBaseProps } from 'react-icons/lib';
import { MdOutlineAutoAwesome, MdOutlineEdit, MdOutlineNotificationsActive } from 'react-icons/md';
import {
  GlobalActivityStateAction,
  Importance,
  Origin,
  UserActivityStateAction
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Design, DesignType, Intent, IntentType } from '@Components/designConstants';
import { useExperimentFlag } from '../../utils/experiment-context';

export { useNotImplemented } from '../../utils/hooks/useNotImplemented';

// Gates rendering of stubbed UI (share/star/pin/more, Create Task, Post comment, etc.). While
// the flag is off, none of the not-yet-implemented affordances render at all — the feed just
// shows read-only cards + detail content.
export const useShowWipActions = (): boolean => Boolean(useExperimentFlag('enableActivityFeedWIP'));

export const formatRelativeActivityDate = (date: Date | string): string =>
  formatDistanceToNow(new Date(date), { addSuffix: true });

export interface ProvenanceDisplay {
  label: string;
  design: DesignType | IntentType;
  icon: ComponentType<IconBaseProps>;
}

const PROVENANCE_BY_ORIGIN: Record<Origin, ProvenanceDisplay> = {
  [Origin.AI]: { label: 'AI-Generated', design: Design.BRAND, icon: MdOutlineAutoAwesome },
  [Origin.DERIVED]: {
    label: 'Automated Alert',
    design: Intent.INFO,
    icon: MdOutlineNotificationsActive
  },
  [Origin.SYSTEM]: {
    label: 'Automated Alert',
    design: Intent.INFO,
    icon: MdOutlineNotificationsActive
  },
  [Origin.MANUAL]: { label: 'Manual', design: Design.NEUTRAL, icon: MdOutlineEdit }
};

export const getProvenanceDisplay = (origin: Origin): ProvenanceDisplay =>
  PROVENANCE_BY_ORIGIN[origin];

export interface ImportanceDisplay {
  label: string;
  activeBarCount: 0 | 1 | 2 | 3;
}

const IMPORTANCE_DISPLAY: Record<Importance, ImportanceDisplay> = {
  [Importance.HIGH]: { label: 'High', activeBarCount: 3 },
  [Importance.MEDIUM]: { label: 'Medium', activeBarCount: 2 },
  [Importance.LOW]: { label: 'Low', activeBarCount: 1 },
  [Importance.NONE]: { label: 'N/A', activeBarCount: 0 }
};

export const getImportanceDisplay = (importance: Importance): ImportanceDisplay =>
  IMPORTANCE_DISPLAY[importance];

// Pair each toggleable flag with its on / off action once so call sites can't transpose them.
// Prefer these over inlining `isStarred ? UNSTAR : STAR` at every button handler.
export const nextStarAction = (isStarred: boolean): UserActivityStateAction =>
  isStarred ? UserActivityStateAction.UNSTAR : UserActivityStateAction.STAR;

export const nextPinAction = (isPinned: boolean): GlobalActivityStateAction =>
  isPinned ? GlobalActivityStateAction.UNPIN : GlobalActivityStateAction.PIN;

// Pre-selects the client when there is only one to choose from.
export const soleClient = (cids: readonly string[]): string | null =>
  cids.length === 1 ? cids[0] : null;
